import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackLink, Spinner } from '../components/ui';

/**
 * Legal pages. The source is docs/legal/*.md, rendered at build time to static HTML
 * (/legal/<doc>.<lang>.html) so that the same public URLs work without JavaScript (Google Play
 * privacy policy and account deletion links). The in-app page displays that same file.
 */
const DOCS: Record<string, readonly string[]> = {
  privacy: ['fr', 'en'],
  'delete-account': ['fr', 'en'],
  terms: ['fr'],
  notice: ['fr'],
};

export function Legal({ params }: { params: { doc: string } }) {
  const { t, i18n } = useTranslation();
  const langs = DOCS[params.doc];
  const lang = langs?.includes(i18n.language.slice(0, 2)) ? i18n.language.slice(0, 2) : 'fr';
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!langs) return;
    setHtml(null);
    fetch(`/legal/${params.doc}.${lang}.html`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((page) => {
        // Our own generated file (same origin, built from the repository): only <main> is shown.
        const body = new DOMParser().parseFromString(page, 'text/html').querySelector('main');
        setHtml(body?.innerHTML ?? '');
      })
      .catch(() => setFailed(true));
  }, [params.doc, lang, langs]);

  if (!langs || failed) return <main className="page">{t('errors.notFound')}</main>;
  if (html === null) return <Spinner label={t('common.loading')} />;
  // Draft until every placeholder has been replaced by the publisher's real information.
  const draft = /\[(À compléter|To complete)/.test(html);
  return (
    <main className="page legal">
      <BackLink to="/about" />
      {draft && <p className="banner warn">{t('legal.draftBanner')}</p>}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </main>
  );
}
